import { useZnglyContext } from '@/Utility/context/ZnglyAppContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Container, Skeleton, Stack } from '@mui/material';
import type React from 'react';
import { graphql, useFragment } from 'react-relay';
import { faParseIconName } from '../../inputs/IconAutocomplete';
import type {
  Header_ZnglyInfo_Desktop$data,
  Header_ZnglyInfo_Desktop$key,
} from './__generated__/Header_ZnglyInfo_Desktop.graphql';

import { parseConditionsJson, useConditionShowOnPage } from '@/Components/leva/utils';
import { getMediaItemUrl } from '@/Components/posts/utils';
import { MediaItemSizeEnum } from '@/graphql/types';
import { PAGE_LAYOUT } from '@/inertia/Shared/Page';
import Suspense from '../../Suspense';
import { HeaderButtons, HeaderDDMenu, HeaderLogoLink } from './utils';

import styles from './styles.module.css';

export const Header_ZnglyInfo_Desktop = graphql`
  fragment Header_ZnglyInfo_Desktop on ZnglyInfo {
    znglyInfo {
      dashboardLink
      headerLogo {
        ...Media_Url_Fragment @relay(mask: false)
      }
      headerText
      headerTextConditions
      headerLogoLink
      headerButtons {
        name
        url
        bgColor
        textColor
        bgHvColor
        textHvColor
        form {
          node {
            ...Frags_Frontend_Form_Fragment @relay(mask: false)
          }
        }
      }
      customSocialLinks {
        name
        url
      }
    }
  }
`;

type Props = {
  znglyInfo: Header_ZnglyInfo_Desktop$data['znglyInfo'];
} & HeaderInputProps;

const Header = ({ style, znglyInfo, ...props }: Props) => {
  const showHeaderText = useConditionShowOnPage(parseConditionsJson(znglyInfo?.headerTextConditions ?? '[]'));

  return (
    <div style={{ borderBottom: '1px solid #d6d6d6' }} className={styles.headerRoot}>
      <Container {...PAGE_LAYOUT.containerProps}>
        <Stack direction="row" className={styles.header} style={style}>
          {/* left header image and txt */}
          <Stack direction="row" className={styles.left}>
            {props?.left?.showLogo !== false && (
              <Box
                sx={{
                  height: '36px',
                  ...(!!znglyInfo?.headerText && { borderRight: '2px solid var(--header-p-text-col)', pr: 1 }),
                }}
              >
                <HeaderLogoLink znglyInfo={znglyInfo}>
                  <img
                    src={getMediaItemUrl(znglyInfo?.headerLogo, MediaItemSizeEnum.Medium) ?? ''}
                    style={{ height: '100%' }}
                  />
                </HeaderLogoLink>
              </Box>
            )}
            {props?.left?.showText !== false && showHeaderText && (
              <Box className={styles.title}>
                <HeaderLogoLink homeOnly znglyInfo={znglyInfo}>
                  {znglyInfo?.headerText}
                </HeaderLogoLink>
              </Box>
            )}
            {props?.left?.showSocialLinks !== false && !!znglyInfo?.customSocialLinks?.length && (
              <Box className={styles.socialLinks}>
                {znglyInfo.customSocialLinks.map((link) => {
                  const icon = faParseIconName(link?.name, 'fab');
                  if (!icon) return <></>;
                  return (
                    <a key={`${link?.name}_${link?.url}`} href={link?.url ?? ''} target="_blank" rel="noreferrer">
                      <FontAwesomeIcon icon={[icon.pkg, icon.label]} />
                    </a>
                  );
                })}
              </Box>
            )}

            {props.left?.element}
          </Stack>

          <Stack direction="row" className={styles.right}>
            {props.right?.element}

            {/* Header Buttons in the middle */}
            {props.right?.showButtons !== false && <HeaderButtons znglyInfo={znglyInfo} type="desktop" />}

            {/* Zngly Powered by */}
            {props.right?.showPoweredBy !== false && (
              <Box className={styles.poweredBy} pr={1}>
                <a href="https://zngly.com/" className="powered-by-zngly">
                  Powered by <b>Zngly</b>
                </a>
              </Box>
            )}

            {/* User DropDown */}
            {props.right?.showUserMenu !== false && <HeaderDDMenu {...props?.right?.userMenu} />}
          </Stack>
        </Stack>
      </Container>
    </div>
  );
};

type HeaderInputProps = {
  style?: React.CSSProperties;
  left?: {
    showLogo?: boolean;
    showText?: boolean;
    showSocialLinks?: boolean;
    element?: React.ReactNode;
  };
  right?: {
    showButtons?: boolean;
    showPoweredBy?: boolean;
    showUserMenu?: boolean;
    element?: React.ReactNode;
    userMenu?: {
      onlyShowIfLoggedIn?: boolean;
      disabled?: boolean;
    };
  };
};

const HeaderFallBackDesktop = (props: HeaderInputProps) => {
  const Skelet = ({ ...p }) => <Skeleton {...p} sx={{ backgroundColor: 'var(--header-p-text-col)' }} />;
  return (
    <div style={{ borderBottom: '1px solid #d6d6d6' }} className={styles.headerRoot}>
      <Container {...PAGE_LAYOUT.containerProps}>
        <Stack direction="row" className={styles.header} style={props?.style}>
          <Stack direction="row" className={styles.left}>
            <Box>
              <Skelet width={30} />
            </Box>
            <Box className={styles.title}>
              <Skelet width={30} />
            </Box>
          </Stack>

          <Stack direction="row" className={styles.right}>
            <Box className={styles.poweredBy} pr={1}>
              <a href="https://www.zngly.com/" className="powered-by-zngly">
                Powered by <b>Zngly</b>
              </a>
            </Box>
            <Box className="User">
              <Skelet variant="circular" width={20} heigth={30} />
            </Box>
          </Stack>
        </Stack>
      </Container>
    </div>
  );
};

const Wrapper = ({
  queryRef,
  ...props
}: HeaderInputProps & { queryRef: ReturnType<typeof useZnglyContext>['appData']['queryRef'] }) => {
  const data = useFragment<Header_ZnglyInfo_Desktop$key>(Header_ZnglyInfo_Desktop, queryRef?.znglyInfo);

  return <Header {...props} znglyInfo={data?.znglyInfo as Header_ZnglyInfo_Desktop$data['znglyInfo']} />;
};

export const HeaderDesktop = (props: HeaderInputProps) => {
  const ctx = useZnglyContext();

  return (
    <Suspense fallback={<HeaderFallBackDesktop {...props} />} exists={ctx?.appData?.queryRef?.znglyInfo}>
      {!!ctx?.appData?.queryRef && <Wrapper {...props} queryRef={ctx.appData.queryRef} />}
    </Suspense>
  );
};
